import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function KnowledgeBase() {
  const [products, setProducts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [voice, setVoice] = useState(null);
  const [samples, setSamples] = useState([]);
  const [newSample, setNewSample] = useState({ title: "", text: "", label: "sounds_exactly_like_me" });

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/knowledge_docs").then((r) => setDocs(r.data));
    api.get("/voice_profile").then((r) => setVoice(r.data));
    api.get("/voice_samples").then((r) => setSamples(r.data));
  };
  useEffect(() => { load(); }, []);

  const addSample = async () => {
    if (!newSample.text) return;
    await api.post("/voice_samples", newSample);
    toast.success("Voice sample saved");
    setNewSample({ title: "", text: "", label: "sounds_exactly_like_me" });
    load();
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-1">Knowledge Base</div>
        <h1 className="text-5xl font-black tracking-tight leading-none" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Brand · Products · Voice</h1>
      </div>

      <section className="mb-10">
        <h2 className="text-2xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Products</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {products.map((p) => (
            <div key={p.id} data-testid={`product-${p.id}`} className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{p.category}</div>
              <div className="font-bold mt-1">{p.name}</div>
              <div className="text-xs text-neutral-500 mt-1">{p.pack_size} · {p.flavour}</div>
              <div className="text-xs text-neutral-600 mt-2"><span className="uppercase font-semibold tracking-widest">Stage:</span> {p.launch_stage}</div>
              {p.approved_claims?.length > 0 && <div className="text-xs text-emerald-800 mt-1">✓ {p.approved_claims.slice(0, 2).join(" · ")}</div>}
              {p.prohibited_claims?.length > 0 && <div className="text-xs text-red-700 mt-1">✗ {p.prohibited_claims.slice(0, 2).join(" · ")}</div>}
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="text-2xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Founder voice</h2>
        {voice && (
          <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Traits</div>
                <div className="flex flex-wrap gap-1">
                  {voice.traits?.map((t, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 border border-neutral-200">{t}</span>)}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Avoid</div>
                <div className="flex flex-wrap gap-1">
                  {voice.avoid?.map((t, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-900">{t}</span>)}
                </div>
              </div>
            </div>
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mt-4 mb-1">Narrative structure</div>
            <div className="text-sm text-neutral-700">{voice.narrative_structure}</div>
          </div>
        )}

        <div className="bg-white border border-neutral-200 rounded-lg p-4 mb-3">
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Add voice sample</div>
          <input placeholder="Title" data-testid="sample-title" value={newSample.title} onChange={(e) => setNewSample({ ...newSample, title: e.target.value })} className="w-full border border-neutral-200 rounded p-2 text-sm mb-2" />
          <textarea data-testid="sample-text" placeholder="Paste founder-voice text" value={newSample.text} onChange={(e) => setNewSample({ ...newSample, text: e.target.value })} rows={3} className="w-full border border-neutral-200 rounded p-2 text-sm mb-2" />
          <div className="flex gap-2">
            <select value={newSample.label} onChange={(e) => setNewSample({ ...newSample, label: e.target.value })} className="border border-neutral-300 rounded px-2 py-1 text-sm">
              <option value="sounds_exactly_like_me">Sounds exactly like me</option>
              <option value="partly_sounds_like_me">Partly sounds like me</option>
              <option value="does_not_sound_like_me">Does not sound like me</option>
              <option value="high_performing_reference">High-performing reference</option>
              <option value="never_imitate">Never imitate</option>
            </select>
            <button data-testid="add-sample-btn" onClick={addSample} className="px-3 py-1 rounded text-sm font-semibold text-neutral-900" style={{ background: "#D4FF00" }}>Save</button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {samples.map((s) => (
            <div key={s.id} className="bg-white border border-neutral-200 rounded p-3">
              <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{s.label.replace(/_/g, " ")}</div>
              <div className="font-semibold text-sm mt-1">{s.title}</div>
              <div className="text-sm text-neutral-600 mt-1 line-clamp-3">{s.text}</div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-black tracking-tight mb-3" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Documents</h2>
        {docs.map((d) => (
          <div key={d.id} className="bg-white border border-neutral-200 rounded p-4 mb-2">
            <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{d.doc_type}</div>
            <div className="font-semibold">{d.title}</div>
            <div className="text-sm text-neutral-600 mt-1">{d.content}</div>
          </div>
        ))}
      </section>
    </div>
  );
}
